import React, { useState, useEffect } from 'react'
import { categoryAPI, categoryFieldAPI, globalCategoryFieldAPI, simplifiedCategoryFieldAPI } from '../services/api'
import { Plus, Edit, Trash2, Save, X, Search, Folder, List, Settings, Eye, Globe, Languages } from 'lucide-react'
import GlobalCategoryFieldsManagement from './GlobalCategoryFieldsManagement'
import FieldSampleSetsManagement from './FieldSampleSetsManagement'

import { useAuth } from '../contexts/AuthContext'
import { isAdmin, showAdminAccessDialog, ADMIN_ACTIONS } from '../utils/adminUtils'

const CategoryManagement = () => {
  const { user } = useAuth()
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [editingCategory, setEditingCategory] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    icon: '',
    color: '#3B82F6',
    parentId: '',
    type: 'template',
    isActive: true
  })
  const [parentCategories, setParentCategories] = useState([])
  const [showSubcategories, setShowSubcategories] = useState({})
  const [showFields, setShowFields] = useState({})
  const [categoryFields, setCategoryFields] = useState({})
  const [showFieldForm, setShowFieldForm] = useState(null)
  const [fieldForm, setFieldForm] = useState({
    key: '',
    label: '',
    fieldType: 'text',
    subcategory_id: '',
    global_field_id: '',
    validation: '',
    sampleValue: '',
    minLength: '',
    maxLength: '',
    isRequired: false,
    defaultValue: '',
    sortOrder: 0
  })
  const [activeTab, setActiveTab] = useState('categories') // 'categories' or 'global-fields'
  const [globalFields, setGlobalFields] = useState([])
  const [showGlobalFieldSelector, setShowGlobalFieldSelector] = useState(false)
  const [editingField, setEditingField] = useState(null)
  const [fieldLoading, setFieldLoading] = useState({})
  const [fieldSaving, setFieldSaving] = useState(false)
  const [fieldDeleting, setFieldDeleting] = useState({})

  // Load categories and global fields
  useEffect(() => {
    fetchCategories()
    fetchGlobalFields()
  }, [])

  const fetchCategories = async () => {
    try {
      setLoading(true)
      setError(null)

      // Fetch all categories
      const response = await categoryAPI.getAll()

      if (response.data.success) {
        const allCategories = response.data.data || []
        setCategories(allCategories)

        // Set parent categories (categories without parentId) and sort by name
        const parents = allCategories.filter(cat => !cat.parentId)
        parents.sort((a, b) => {
          const nameA = String(a.name || '').toLowerCase()
          const nameB = String(b.name || '').toLowerCase()
          return nameA.localeCompare(nameB)
        })
        setParentCategories(parents)
      } else {
        setError(response.data.message || 'Failed to fetch categories')
      }
    } catch (error) {
      console.error('Error fetching categories:', error)
      setError('Failed to load categories')
    } finally {
      setLoading(false)
    }
  }

  const fetchGlobalFields = async () => {
    try {
      const response = await globalCategoryFieldAPI.getAll()
      if (response.data.success) {
        const fields = response.data.data.fields || []
        // Sort by category first, then by name/label within each category
        const sortedFields = [...fields].sort((a, b) => {
          // First, sort by category (null/empty categories go to the end)
          const categoryA = (a.category || '').toLowerCase()
          const categoryB = (b.category || '').toLowerCase()
          
          if (categoryA !== categoryB) {
            // If one has category and other doesn't, category comes first
            if (!categoryA) return 1
            if (!categoryB) return -1
            return categoryA.localeCompare(categoryB)
          }
          
          // Within same category (or both without category), sort by name/label
          const nameA = (a.label || a.name || a.key || '').toLowerCase()
          const nameB = (b.label || b.name || b.key || '').toLowerCase()
          return nameA.localeCompare(nameB)
        })
        setGlobalFields(sortedFields)
      }
    } catch (error) {
      console.error('Error fetching global fields:', error)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    // Check admin access
    if (!isAdmin(user)) {
      showAdminAccessDialog(editingCategory ? ADMIN_ACTIONS.UPDATE_CATEGORY : ADMIN_ACTIONS.CREATE_CATEGORY)
      return
    }

    try {
      setLoading(true)
      
      // Ensure slug is set if empty and prepare submit data
      // Convert camelCase to snake_case for backend API
      const parentId = formData.parentId || null
      const submitData = { 
        name: formData.name,
        slug: formData.slug,
        description: formData.description || null,
        icon: formData.icon || null,
        color: formData.color,
        parent_id: parentId,  // Backend expects snake_case
        type: formData.type,
        is_active: formData.isActive
      }
      
      if (!submitData.slug && submitData.name) {
        const parentSlug = parentId 
          ? parentCategories.find(cat => cat.id === parentId)?.slug || 
            parentCategories.find(cat => cat.id === parentId)?.name?.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
          : null
        const nameSlug = submitData.name?.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
        submitData.slug = parentSlug ? `${parentSlug}-${nameSlug}` : nameSlug
      }
      
      // Clean up slug - remove trailing dashes
      if (submitData.slug) {
        submitData.slug = submitData.slug.replace(/(^-|-$)/g, '')
      }
      
      console.log('Submitting category data:', submitData) // Debug log
      
      let response

      if (editingCategory) {
        response = await categoryAPI.update(editingCategory.id, submitData)
      } else {
        response = await categoryAPI.create(submitData)
      }

      if (response.data.success) {
        await fetchCategories()
        resetForm()
        setShowCreateForm(false)
        setEditingCategory(null)

        // Show success notification
        window.dispatchEvent(new CustomEvent('app:notify', {
          detail: {
            type: 'success',
            message: `Category ${editingCategory ? 'updated' : 'created'} successfully!`
          }
        }))
      } else {
        setError(response.data.message || 'Failed to save category')
      }
    } catch (error) {
      console.error('Error saving category:', error)

      // Check if it's an admin access error
      if (error.response?.status === 403 || error.response?.status === 401) {
        showAdminAccessDialog(editingCategory ? ADMIN_ACTIONS.UPDATE_CATEGORY : ADMIN_ACTIONS.CREATE_CATEGORY)
      } else {
        setError(error.response?.data?.message || 'Failed to save category')

        // Show error notification
        window.dispatchEvent(new CustomEvent('app:notify', {
          detail: {
            type: 'error',
            message: error.response?.data?.message || 'Failed to save category. Please try again.'
          }
        }))
      }
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async (category_id) => {
    if (!window.confirm('Are you sure you want to delete this category?')) {
      return
    }

    try {
      setLoading(true)
      const response = await categoryAPI.delete(category_id)

      if (response.data.success) {
        await fetchCategories()
      } else {
        setError(response.data.message || 'Failed to delete category')
      }
    } catch (error) {
      console.error('Error deleting category:', error)
      setError('Failed to delete category')
    } finally {
      setLoading(false)
    }
  }

  const handleEdit = (category) => {
    setEditingCategory(category)
    setFormData({
      name: category.name || '',
      slug: category.slug || '',
      description: category.description || '',
      icon: category.icon || '',
      color: category.color || '#3B82F6',
      parentId: category.parentId || '',
      type: category.type || 'template',
      isActive: category.isActive !== false
    })
    setShowCreateForm(true)
  }

  const resetForm = () => {
    setFormData({
      name: '',
      slug: '',
      description: '',
      icon: '',
      color: '#3B82F6',
      parentId: '',
      type: 'template',
      isActive: true
    })
  }

  const filteredCategories = categories.filter(category =>
    category.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    category.description?.toLowerCase().includes(searchTerm.toLowerCase())
  )

  // Helper functions for subcategory management
  const getSubcategories = (parentId) => {
    const subcats = categories.filter(cat => cat.parentId === parentId)
    // Sort subcategories by name
    return subcats.sort((a, b) => {
      const nameA = String(a.name || '').toLowerCase()
      const nameB = String(b.name || '').toLowerCase()
      return nameA.localeCompare(nameB)
    })
  }

  const toggleSubcategories = (category_id) => {
    setShowSubcategories(prev => ({
      ...prev,
      [category_id]: !prev[category_id]
    }))
  }

  const toggleFields = async (category_id) => {
    if (!showFields[category_id]) {
      // Load fields when expanding
      setFieldLoading(prev => ({ ...prev, [category_id]: true }))
      try {
        const res = await categoryFieldAPI.listForCategory(category_id)
        if (res.data.success) {
          setCategoryFields(prev => ({
            ...prev,
            [category_id]: res.data.data
          }))
        }
      } catch (e) {
        setError('Failed to load fields')
      } finally {
        setFieldLoading(prev => ({ ...prev, [category_id]: false }))
      }
    }
    setShowFields(prev => ({
      ...prev,
      [category_id]: !prev[category_id]
    }))
  }

  const resetFieldForm = () => {
    setFieldForm({
      key: '',
      label: '',
      fieldType: 'text',
      subcategory_id: '',
      global_field_id: '',
      validation: '',
      sampleValue: '',
      minLength: '',
      maxLength: '',
      isRequired: false,
      defaultValue: '',
      sortOrder: 0
    })
    setEditingField(null)
    setShowGlobalFieldSelector(false)
  }

  const handleFieldSubmit = async (e, category_id) => {
    e.preventDefault()
    setFieldSaving(true)
    try {
      if (fieldForm.global_field_id) {
        // Use simplified API for global field assignment
        await simplifiedCategoryFieldAPI.assignGlobalField({
          category_id: category_id,
          global_field_id: fieldForm.global_field_id,
          is_required: fieldForm.isRequired,
          sort_order: fieldForm.sortOrder
        })
      } else {
        // Use legacy API for custom fields
        await categoryFieldAPI.create({ ...fieldForm, category_id })
      }
      // Reload fields
      const res = await categoryFieldAPI.listForCategory(category_id)
      if (res.data.success) {
        setCategoryFields(prev => ({
          ...prev,
          [category_id]: res.data.data
        }))
      }
      resetFieldForm()
      setShowFieldForm(null)
      setSuccess('Field added successfully')
      setError(null)
    } catch (e) {
      setError(e.response?.data?.detail || e.response?.data?.message || 'Failed to save field')
      setSuccess(null)
    } finally {
      setFieldSaving(false)
    }
  }

  const handleFieldEdit = (field) => {
    setEditingField(field)
    setFieldForm({
      key: field.key || '',
      label: field.label || '',
      fieldType: field.fieldType || 'text',
      subcategory_id: field.subcategory_id || '',
      global_field_id: field.global_field_id || '',
      validation: field.validation || '',
      sampleValue: field.sampleValue || '',
      minLength: field.minLength || '',
      maxLength: field.maxLength || '',
      isRequired: field.isRequired || false,
      defaultValue: field.defaultValue || '',
      sortOrder: field.sortOrder || 0
    })
    setShowFieldForm(field.category_id)
    setShowGlobalFieldSelector(!!field.global_field_id)
  }

  const handleGlobalFieldSelect = (globalField) => {
    setFieldForm(prev => ({
      ...prev,
      global_field_id: globalField.id,
      key: globalField.key,
      label: globalField.label,
      fieldType: globalField.fieldType,
      validation: globalField.validation || '',
      minLength: globalField.minLength || '',
      maxLength: globalField.maxLength || '',
      isRequired: globalField.isRequired || false,
      sampleValue: globalField.sampleValue || ''
    }))
    setShowGlobalFieldSelector(false)
  }

  const handleFieldDelete = async (fieldId, category_id) => {
    if (!window.confirm('Delete this field?')) return
    setFieldDeleting(prev => ({ ...prev, [fieldId]: true }))
    try {
      await categoryFieldAPI.delete(fieldId)
      // Reload fields
      const res = await categoryFieldAPI.listForCategory(category_id)
      if (res.data.success) {
        setCategoryFields(prev => ({
          ...prev,
          [category_id]: res.data.data
        }))
      }
    } catch (e) {
      setError('Failed to delete field')
    } finally {
      setFieldDeleting(prev => ({ ...prev, [fieldId]: false }))
    }
  }

  const createSubcategory = (parentCategory) => {
    const parentSlug = parentCategory.slug || parentCategory.name?.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
    setFormData({
      name: '',
      slug: parentSlug ? `${parentSlug}-` : '',
      description: '',
      icon: '',
      color: '#3B82F6',
      parentId: parentCategory.id,
      type: parentCategory.type || 'template',
      isActive: true
    })
    setEditingCategory(null)
    setShowCreateForm(true)
  }

  if (loading && categories.length === 0) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-16 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Category & Field Management</h1>
        <div className="flex space-x-2">
          {activeTab === 'categories' && (
            <button
              onClick={() => {
                resetForm()
                setEditingCategory(null)
                setShowCreateForm(true)
              }}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Plus className="w-4 h-4" />
              <span>Add Category</span>
            </button>
          )}
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="mb-6">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8">
            <button
              onClick={() => setActiveTab('categories')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'categories'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Folder className="w-4 h-4 inline mr-2" />
              Categories & Fields
            </button>

            <button
              onClick={() => setActiveTab('global-fields')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'global-fields'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Globe className="w-4 h-4 inline mr-2" />
              Global Fields
            </button>

            <button
              onClick={() => setActiveTab('field-samples')}
              className={`py-2 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'field-samples'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <List className="w-4 h-4 inline mr-2" />
              Field Samples
            </button>
          </nav>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-600">{error}</p>
          <button
            onClick={() => setError(null)}
            className="mt-2 text-sm text-red-500 hover:text-red-700"
          >
            Dismiss
          </button>
        </div>
      )}

      {success && (
        <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
          <p className="text-green-600">{success}</p>
          <button
            onClick={() => setSuccess(null)}
            className="mt-2 text-sm text-green-500 hover:text-green-700"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Tab Content */}
      {activeTab === 'global-fields' ? (
        <GlobalCategoryFieldsManagement />
      ) : activeTab === 'field-samples' ? (
        <FieldSampleSetsManagement />
      ) : (
        <>
          {/* Search */}
          <div className="mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search categories..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

      {/* Create/Edit Form */}
      {showCreateForm && (
        <div className="mb-6 p-6 bg-gray-50 rounded-lg border">
          <h3 className="text-lg font-medium mb-4">
            {editingCategory ? 'Edit Category' : 'Create New Category'}
          </h3>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Slug
              </label>
              <input
                type="text"
                value={formData.slug}
                onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                onBlur={() => {
                  if (!formData.slug && formData.name) {
                    const parentSlug = formData.parentId 
                      ? parentCategories.find(cat => cat.id === formData.parentId)?.slug || 
                        parentCategories.find(cat => cat.id === formData.parentId)?.name?.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
                      : null
                    const nameSlug = formData.name?.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
                    setFormData(prev => ({
                      ...prev,
                      slug: parentSlug ? `${parentSlug}-${nameSlug}` : nameSlug
                    }))
                  }
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder={formData.parentId ? "parent-slug-subcategory-name" : "auto-generated-from-name-if-empty"}
              />
              {formData.parentId && (
                <p className="text-xs text-gray-500 mt-1">
                  Slug will be prefixed with parent category slug
                </p>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => {
                    const newName = e.target.value
                    // If slug is empty or matches the old name pattern, auto-update it
                    const parentSlug = formData.parentId 
                      ? parentCategories.find(cat => cat.id === formData.parentId)?.slug || 
                        parentCategories.find(cat => cat.id === formData.parentId)?.name?.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
                      : null
                    const nameSlug = newName?.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
                    setFormData({ 
                      ...formData, 
                      name: newName,
                      slug: formData.slug === '' || (formData.parentId && formData.slug === `${parentSlug}-`) 
                        ? (parentSlug ? `${parentSlug}-${nameSlug}` : nameSlug)
                        : formData.slug
                    })
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Category name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Icon
                </label>
                <input
                  type="text"
                  value={formData.icon}
                  onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="🏷️ (emoji or icon class)"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                rows="3"
                placeholder="Category description"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Parent Category
                </label>
                <select
                  value={formData.parentId}
                  onChange={(e) => {
                    const selectedParentId = e.target.value
                    const selectedParent = parentCategories.find(cat => cat.id === selectedParentId)
                    const parentSlug = selectedParent?.slug || selectedParent?.name?.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
                    setFormData({ 
                      ...formData, 
                      parentId: selectedParentId,
                      slug: selectedParentId && parentSlug ? `${parentSlug}-` : formData.slug,
                      type: selectedParent?.type || formData.type
                    })
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">None (Main Category)</option>
                  {parentCategories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Leave empty to create a main category, or select a parent to create a subcategory
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Type
                </label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="template">Template</option>
                  <option value="photo">Photo</option>
                  <option value="graphic">Graphic</option>
                  <option value="video">Video</option>
                  <option value="audio">Audio</option>
                  <option value="pdf">PDF</option>
                  <option value="speech">Speech</option>
                  <option value="tts">tts</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Color
                </label>
                <input
                  type="color"
                  value={formData.color}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                  className="w-full h-10 border border-gray-300 rounded-lg"
                />
              </div>

              <div className="flex items-center">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700">Active</span>
                </label>
              </div>
            </div>

            <div className="flex space-x-3">
              <button
                type="submit"
                disabled={loading}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                <Save className="w-4 h-4" />
                <span>{editingCategory ? 'Update' : 'Create'}</span>
              </button>

              <button
                type="button"
                onClick={() => {
                  setShowCreateForm(false)
                  setEditingCategory(null)
                  resetForm()
                }}
                className="flex items-center space-x-2 px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
              >
                <X className="w-4 h-4" />
                <span>Cancel</span>
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Categories List */}
      <div className="bg-white rounded-lg border">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium">Categories ({filteredCategories.length})</h3>
        </div>

        <div className="divide-y divide-gray-200">
          {filteredCategories.length > 0 ? (
            // Group categories by type
            (() => {
              const mainCategories = filteredCategories.filter(category => !category.parentId)
              const categoriesByType = mainCategories.reduce((acc, category) => {
                const type = category.type || 'template'
                if (!acc[type]) {
                  acc[type] = []
                }
                acc[type].push(category)
                return acc
              }, {})

              const typeOrder = ['template', 'photo', 'graphic', 'video', 'audio', 'pdf', 'speech', 'tts']
              const sortedTypes = typeOrder.filter(type => categoriesByType[type]).concat(
                Object.keys(categoriesByType).filter(type => !typeOrder.includes(type))
              )

              return sortedTypes.map((type) => (
                <div key={type} className="border-b border-gray-200 last:border-b-0">
                  <div className="px-6 py-3 bg-gray-50 border-b border-gray-200">
                    <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                      {type.charAt(0).toUpperCase() + type.slice(1)} ({categoriesByType[type].length})
                    </h4>
                  </div>
                  {categoriesByType[type].map((category) => (
                    <div key={category.id}>
                      {/* Main Category */}
                      <div className="px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div
                        className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium"
                        style={{ backgroundColor: category.color || '#3B82F6' }}
                      >
                        {category.icon || category.name?.charAt(0)?.toUpperCase()}
                      </div>

                      <div>
                        <h4 className="font-medium text-gray-900">{category.name}</h4>
                        {category.description && (
                          <p className="text-sm text-gray-500">{category.description}</p>
                        )}
                        <div className="flex items-center space-x-2 mt-1">
                          <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                            {category.type || 'template'}
                          </span>
                          <span className={`text-xs px-2 py-1 rounded ${
                            category.isActive
                              ? 'bg-green-100 text-green-800'
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {category.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center space-x-2">
                      {getSubcategories(category.id).length > 0 && (
                        <button
                          onClick={() => toggleSubcategories(category.id)}
                          className="px-3 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                        >
                          {showSubcategories[category.id] ? 'Hide' : 'Show'} Subcategories ({getSubcategories(category.id).length})
                        </button>
                      )}

                      <button
                        onClick={() => createSubcategory(category)}
                        className="flex items-center space-x-1 px-3 py-1 text-sm text-gray-600 hover:text-green-600 rounded-lg hover:bg-green-50 border border-gray-200"
                        title="Add Subcategory"
                      >
                        <Plus className="w-3 h-3" />
                        <span>Add Sub</span>
                      </button>

                      <button
                        onClick={() => handleEdit(category)}
                        className="flex items-center space-x-1 px-3 py-1 text-sm text-gray-600 hover:text-blue-600 rounded-lg hover:bg-blue-50 border border-gray-200"
                        title="Edit Category"
                      >
                        <Edit className="w-3 h-3" />
                        <span>Edit</span>
                      </button>

                      {/* Field management buttons only for template parent categories */}
                      {!category.parentId && category.type === 'template' && (
                        <>
                          <button
                            onClick={() => toggleFields(category.id)}
                            className="flex items-center space-x-1 px-3 py-1 text-sm text-gray-600 hover:text-purple-600 rounded-lg hover:bg-purple-50 disabled:opacity-50 border border-gray-200"
                            title="View Fields"
                            disabled={fieldLoading[category.id]}
                          >
                            {fieldLoading[category.id] ? (
                              <div className="w-3 h-3 border-2 border-purple-600 border-t-transparent rounded-full animate-spin"></div>
                            ) : (
                              <Eye className="w-3 h-3" />
                            )}
                            <span>Fields</span>
                          </button>
                          <button
                            onClick={() => {
                              resetFieldForm()
                              setShowFieldForm(category.id)
                            }}
                            className="flex items-center space-x-1 px-3 py-1 text-sm text-gray-600 hover:text-green-600 rounded-lg hover:bg-green-50 disabled:opacity-50 border border-gray-200"
                            title="Add Field"
                            disabled={fieldSaving}
                          >
                            <Settings className="w-3 h-3" />
                            <span>Add Field</span>
                          </button>
                        </>
                      )}

                      <button
                        onClick={() => handleDelete(category.id)}
                        className="flex items-center space-x-1 px-3 py-1 text-sm text-gray-600 hover:text-red-600 rounded-lg hover:bg-red-50 border border-gray-200"
                        title="Delete Category"
                      >
                        <Trash2 className="w-3 h-3" />
                        <span>Delete</span>
                      </button>
                    </div>
                  </div>

                  {/* Fields */}
                  {showFields[category.id] && (
                    <div className="mt-4 ml-8 space-y-2">
                      <h4 className="text-sm font-medium text-gray-700">Category Fields</h4>
                      {fieldLoading[category.id] ? (
                        <div className="flex items-center space-x-2 p-3 bg-gray-50 rounded-lg">
                          <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                          <span className="text-sm text-gray-600">Loading fields...</span>
                        </div>
                      ) : categoryFields[category.id]?.length > 0 ? (
                        <div className="space-y-2">
                          {categoryFields[category.id].map(field => (
                            <div key={field.id} className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border">
                              <div>
                                <div className="font-medium text-sm">{field.label} <span className="text-gray-500">({field.key})</span></div>
                                <div className="text-xs text-gray-500">
                                  {field.fieldType}{field.validation ? ` (${field.validation})` : ''}{field.isRequired ? ' • required' : ''}
                                  {field.minLength && field.maxLength ? ` • ${field.minLength}-${field.maxLength} chars` : ''}
                                  {field.sampleValue ? ` • sample: ${field.sampleValue}` : ''}
                                  {field.defaultValue ? ` • default: ${field.defaultValue}` : ''}
                                </div>
                              </div>
                              <div className="flex items-center space-x-1">
                                <button
                                  onClick={() => handleFieldEdit(field)}
                                  className="flex items-center space-x-1 px-2 py-1 text-xs text-blue-600 hover:text-blue-800 rounded disabled:opacity-50 border border-blue-200"
                                  title="Edit Field"
                                  disabled={fieldSaving || fieldDeleting[field.id]}
                                >
                                  <Edit className="w-3 h-3" />
                                  <span>Edit</span>
                                </button>
                                <button
                                  onClick={() => handleFieldDelete(field.id, category.id)}
                                  className="flex items-center space-x-1 px-2 py-1 text-xs text-red-600 hover:text-red-800 rounded disabled:opacity-50 border border-red-200"
                                  title="Delete Field"
                                  disabled={fieldSaving || fieldDeleting[field.id]}
                                >
                                  {fieldDeleting[field.id] ? (
                                    <div className="w-3 h-3 border border-red-600 border-t-transparent rounded-full animate-spin"></div>
                                  ) : (
                                    <Trash2 className="w-3 h-3" />
                                  )}
                                  <span>Delete</span>
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500">No fields yet</p>
                      )}
                    </div>
                  )}

                  {/* Field Form */}
                  {showFieldForm === category.id && (
                    <div className="mt-4 ml-8 p-4 bg-gray-50 rounded-lg border">
                      <h4 className="text-sm font-medium text-gray-700 mb-3">
                        Add Field
                      </h4>
                      <form onSubmit={(e) => handleFieldSubmit(e, category.id)} className="space-y-3">
                        {/* Global Field Selector */}
                        <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                          <div className="flex items-center justify-between mb-2">
                            <label className="text-sm font-medium text-blue-700">Use Global Field</label>
                            <button
                              type="button"
                              onClick={() => setShowGlobalFieldSelector(!showGlobalFieldSelector)}
                              className="text-xs text-blue-600 hover:text-blue-800"
                            >
                              {showGlobalFieldSelector ? 'Hide Global Fields' : 'Browse Global Fields'}
                            </button>
                          </div>

                          {showGlobalFieldSelector && (
                            <div className="max-h-40 overflow-y-auto border border-gray-200 rounded bg-white">
                              {globalFields.length === 0 ? (
                                <p className="p-3 text-sm text-gray-500">No global fields available</p>
                              ) : (
                                globalFields.map(field => (
                                  <div
                                    key={field.id}
                                    onClick={() => handleGlobalFieldSelect(field)}
                                    className="p-2 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                                  >
                                    <div className="flex items-center justify-between">
                                      <div>
                                        <span className="text-sm font-medium">{field.label}</span>
                                        <span className="text-xs text-gray-500 ml-2">({field.key})</span>
                                      </div>
                                      <div className="flex items-center space-x-1">
                                        <Globe className="w-3 h-3 text-blue-500" />
                                        {field.category && (
                                          <span className="text-xs bg-blue-100 text-blue-800 px-1 rounded">
                                            {field.category}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                ))
                              )}
                            </div>
                          )}

                          {fieldForm.global_field_id && (
                            <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded">
                              <div className="flex items-center justify-between">
                                <span className="text-sm text-green-700">
                                  Using global field: <strong>{fieldForm.label}</strong>
                                </span>
                                <button
                                  type="button"
                                  onClick={() => setFieldForm({ ...fieldForm, global_field_id: '', key: '', label: '' })}
                                  className="text-xs text-red-600 hover:text-red-800"
                                >
                                  Remove
                                </button>
                              </div>
                            </div>
                          )}
                        </div>


                        <div className="flex items-center space-x-4">
                          <label className="flex items-center space-x-2 text-sm">
                            <input
                              type="checkbox"
                              checked={fieldForm.isRequired}
                              onChange={(e) => setFieldForm({ ...fieldForm, isRequired: e.target.checked })}
                            />
                            <span>Required</span>
                          </label>
                          <div className="flex items-center space-x-2">
                            <label className="text-xs text-gray-600">Sort Order:</label>
                            <input
                              type="number"
                              value={fieldForm.sortOrder}
                              onChange={(e) => setFieldForm({ ...fieldForm, sortOrder: Number(e.target.value) })}
                              className="w-16 px-2 py-1 text-sm border rounded"
                            />
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            type="submit"
                            disabled={fieldSaving}
                            className="flex items-center space-x-1 px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {fieldSaving ? (
                              <div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin"></div>
                            ) : (
                              <Save className="w-3 h-3" />
                            )}
                            <span>{fieldSaving ? 'Saving...' : 'Create'}</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setShowFieldForm(null)
                              resetFieldForm()
                            }}
                            disabled={fieldSaving}
                            className="flex items-center space-x-1 px-3 py-1 bg-gray-200 text-sm rounded disabled:opacity-50"
                          >
                            <X className="w-3 h-3" />
                            <span>Cancel</span>
                          </button>
                        </div>
                      </form>
                    </div>
                  )}

                      {/* Subcategories */}
                      {showSubcategories[category.id] && (
                        <div className="bg-gray-50 border-t">
                          {getSubcategories(category.id).map((subcategory) => (
                            <div key={subcategory.id} className="px-6 py-3 ml-8 flex items-center justify-between border-l-4 border-blue-300 bg-blue-50/30">
                              <div className="flex items-center space-x-4">
                                <div className="flex items-center space-x-2">
                                  <div className="w-2 h-2 rounded-full bg-blue-400"></div>
                                  <div
                                    className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-medium"
                                    style={{ backgroundColor: subcategory.color || '#6B7280' }}
                                  >
                                    {subcategory.icon || subcategory.name?.charAt(0)?.toUpperCase()}
                                  </div>
                                </div>

                                <div>
                                  <div className="flex items-center space-x-2">
                                    <h5 className="text-sm font-medium text-gray-900">{subcategory.name}</h5>
                                    <span className="text-xs text-gray-400">({category.name})</span>
                                  </div>
                                  {subcategory.description && (
                                    <p className="text-xs text-gray-500 mt-1">{subcategory.description}</p>
                                  )}
                                  <div className="flex items-center space-x-2 mt-1">
                                    <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                                      {subcategory.type || 'template'}
                                    </span>
                                    <span className={`text-xs px-2 py-1 rounded ${
                                      subcategory.isActive
                                        ? 'bg-green-100 text-green-800'
                                        : 'bg-red-100 text-red-800'
                                    }`}>
                                      {subcategory.isActive ? 'Active' : 'Inactive'}
                                    </span>
                                    {subcategory.slug && (
                                      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded font-mono">
                                        {subcategory.slug}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>

                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={() => handleEdit(subcategory)}
                                  className="flex items-center space-x-1 px-2 py-1 text-xs text-gray-600 hover:text-blue-600 rounded hover:bg-blue-50 border border-gray-200"
                                >
                                  <Edit className="w-3 h-3" />
                                  <span>Edit</span>
                                </button>

                                <button
                                  onClick={() => handleDelete(subcategory.id)}
                                  className="flex items-center space-x-1 px-2 py-1 text-xs text-gray-600 hover:text-red-600 rounded hover:bg-red-50 border border-gray-200"
                                >
                                  <Trash2 className="w-3 h-3" />
                                  <span>Delete</span>
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ))
            })()
          ) : (
            <div className="px-6 py-12 text-center">
              <Folder className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No categories found</h3>
              <p className="text-gray-500 mb-4">
                {searchTerm ? 'No categories match your search.' : 'Get started by creating your first category.'}
              </p>
              {!searchTerm && (
                <button
                  onClick={() => {
                    resetForm()
                    setEditingCategory(null)
                    setShowCreateForm(true)
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Create Category
                </button>
              )}
            </div>
          )}
        </div>
      </div>
        </>
      )}
    </div>
  )
}

export default CategoryManagement
